import {
  HHAlert,
  HHButton,
  HHStack,
} from '@hinge-health/react-component-library';
import { Collapse, SxProps, Theme, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { nextStep, prevStep, StepId, stepIndex } from '../../constants';
import { useWizardState } from '../../context/wizard-state.context';
import {
  MOTION_DURATION,
  MOTION_EASING,
  usePrefersReducedMotion,
} from '../motion/motion';

export type DisabledSeverity = 'info' | 'warning' | 'error';
export interface DisabledInfo {
  message: string;
  severity: DisabledSeverity;
}

interface StepNavProps {
  id: StepId;
  canContinue?: boolean;
  continueLabel?: string;
  hideBack?: boolean;
  disabledInfo?: DisabledInfo;
}

const isInteractiveTarget = (el: EventTarget | null): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
};

export const StepNav = ({
  id,
  canContinue = true,
  continueLabel = 'Continue →',
  hideBack,
  disabledInfo,
}: StepNavProps): JSX.Element => {
  const theme = useTheme();
  const reduced = usePrefersReducedMotion();
  const navigate = useNavigate();
  const { setState } = useWizardState();
  const prev = prevStep(id);
  const next = nextStep(id);

  // Tactile press feedback shared across the action buttons: a quick dip on
  // pointer-down that springs back on release. Skipped under reduced motion and
  // never applies while disabled.
  const pressSx: SxProps<Theme> = reduced
    ? {}
    : {
        transition: `transform ${MOTION_DURATION.feedback}ms ${MOTION_EASING.standard}`,
        '&:active:not(:disabled)': { transform: 'scale(0.97)' },
      };

  const handleContinue = (): void => {
    if (next === undefined) return;
    setState(s =>
      stepIndex(next) > stepIndex(s.furthestUnlockedStep)
        ? { ...s, furthestUnlockedStep: next }
        : s,
    );
    navigate(`../${next}`);
  };

  // Arrow keys mirror the buttons exactly: ArrowRight uses the same
  // canContinue gate + furthestUnlockedStep bump as the Continue handler,
  // ArrowLeft does the same nav as Back. Putting the listener here — instead
  // of in WizardShell — means the shortcut sees the current step's
  // canContinue directly, not the stale furthestUnlockedStep proxy.
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (isInteractiveTarget(e.target)) return;
      if (e.key === 'ArrowRight') {
        if (next === undefined || !canContinue) return;
        setState(s =>
          stepIndex(next) > stepIndex(s.furthestUnlockedStep)
            ? { ...s, furthestUnlockedStep: next }
            : s,
        );
        navigate(`../${next}`);
      } else if (e.key === 'ArrowLeft') {
        if (!hideBack && prev !== undefined) navigate(`../${prev}`);
      }
    };
    window.addEventListener('keydown', handler);
    return (): void => window.removeEventListener('keydown', handler);
  }, [next, prev, canContinue, hideBack, navigate, setState]);

  const showReason = !canContinue && disabledInfo != null;

  // The alert lives in normal page flow; only the action row is sticky. This
  // keeps the footer height constant (~64px) regardless of how long the
  // disabled-reason copy gets.
  return (
    <HHStack spacing={0} sx={{ mt: 'auto' }}>
      <Collapse in={showReason} timeout={reduced ? 0 : MOTION_DURATION.state}>
        {disabledInfo != null && (
          <HHAlert
            hhVariant="outlined"
            severity={disabledInfo.severity}
            sx={{ mb: 1 }}
          >
            {disabledInfo.message}
          </HHAlert>
        )}
      </Collapse>
      <HHStack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
        sx={{
          position: 'sticky',
          bottom: 0,
          pt: 2,
          pb: 1,
          background: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          zIndex: 1,
        }}
      >
        {id !== 'intake' && (
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            color="secondary"
            onClick={(): void => navigate('../intake')}
            sx={pressSx}
          >
            Cancel
          </HHButton>
        )}
        {!hideBack && prev !== undefined && (
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            color="primary"
            onClick={(): void => navigate(`../${prev}`)}
            sx={pressSx}
          >
            ← Back
          </HHButton>
        )}
        {next !== undefined && (
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            color="primary"
            disabled={!canContinue}
            onClick={handleContinue}
            sx={pressSx}
          >
            {continueLabel}
          </HHButton>
        )}
      </HHStack>
    </HHStack>
  );
};
